/**
 * Governance Document Template
 * ============================
 * Reusable template for all governance documentation pages.
 * Provides consistent structure, print support, and metadata display.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PHSCommandLayout } from '@/components/phs/enterprise';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  ChevronLeft, 
  Printer, 
  Search, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  ChevronRight,
  FileText,
  BookOpen
} from 'lucide-react';

/**
 * Document section component
 */
export function DocumentSection({ title, icon: Icon, children, id, status = 'active' }) {
  return (
    <section id={id} className="mb-8 scroll-mt-20">
      <div className="flex items-center gap-3 mb-4">
        {Icon && (
          <div className="p-2 rounded-lg bg-emerald-900/30">
            <Icon className="h-5 w-5 text-emerald-400" />
          </div>
        )}
        <h2 className="text-xl font-semibold text-white">{title}</h2>
        {status === 'draft' && (
          <Badge variant="outline" className="bg-amber-500/20 text-amber-300 border-amber-500/50">
            Draft
          </Badge>
        )}
      </div>
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6">
        <div className="prose prose-invert prose-slate max-w-none">
          {children}
        </div>
      </div>
    </section>
  );
}

/**
 * SOP step component
 */
export function SOPStep({ number, title, description, substeps = [] }) {
  return (
    <div className="flex gap-4 mb-4 last:mb-0">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold text-sm">
        {number}
      </div>
      <div className="flex-1">
        <h4 className="text-white font-medium mb-1">{title}</h4>
        <p className="text-slate-400 text-sm mb-2">{description}</p>
        {substeps.length > 0 && (
          <ul className="space-y-1 ml-4">
            {substeps.map((step, idx) => (
              <li key={idx} className="text-slate-400 text-sm flex items-start gap-2">
                <span className="text-emerald-400 mt-1">•</span>
                {step}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * Checklist component
 */
export function Checklist({ title, items }) {
  return (
    <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
      <h4 className="text-white font-medium mb-3 flex items-center gap-2">
        <CheckCircle className="h-4 w-4 text-emerald-400" />
        {title}
      </h4>
      <div className="space-y-2">
        {items.map((item, idx) => (
          <label key={idx} className="flex items-start gap-3 cursor-pointer group">
            <input 
              type="checkbox" 
              className="mt-1 rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
            />
            <span className="text-slate-300 text-sm group-hover:text-white transition">
              {item}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * Escalation path component
 */
export function EscalationPath({ levels }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      {levels.map((level, idx) => (
        <React.Fragment key={idx}>
          <div className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
            idx === 0 ? 'bg-emerald-600 text-white' :
            idx === levels.length - 1 ? 'bg-red-600 text-white' :
            'bg-slate-700 text-slate-200'
          }`}>
            {level}
          </div>
          {idx < levels.length - 1 && (
            <ChevronRight className="h-4 w-4 text-slate-500" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/**
 * Warning/Important note box
 */
export function WarningBox({ title, children }) {
  return (
    <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="text-amber-300 font-medium mb-1">{title}</h4>
          <div className="text-amber-200/80 text-sm">{children}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Info box
 */
export function InfoBox({ title, children }) {
  return (
    <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3">
        <FileText className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="text-blue-300 font-medium mb-1">{title}</h4>
          <div className="text-blue-200/80 text-sm">{children}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Table of contents
 */
export function TableOfContents({ sections }) {
  return (
    <nav className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 mb-8">
      <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
        Contents
      </h3>
      <ul className="space-y-2">
        {sections.map((section, idx) => (
          <li key={idx}>
            <a 
              href={`#${section.id}`}
              className="text-slate-300 hover:text-emerald-400 transition text-sm flex items-center gap-2"
            >
              <span className="text-slate-500">{idx + 1}.</span>
              {section.title}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * Main governance page layout wrapper
 */
export default function GovernancePageLayout({ 
  title, 
  subtitle,
  icon: Icon,
  version = '1.0',
  lastReviewed = '2026-05-23',
  reviewedBy = 'Operations Team',
  status = 'current',
  sections = [],
  children 
}) {
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <PHSCommandLayout>
      <div className="min-h-screen bg-[#0f1a19]" data-testid="governance-page">
        {/* Header */}
        <div className="border-b border-slate-700/50 bg-[#162624] px-6 py-6">
          <div className="max-w-5xl mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
              <Link to="/wyldecrest/helpdesk/governance" className="hover:text-emerald-400 transition flex items-center gap-1">
                <ChevronLeft className="h-4 w-4" />
                Governance Hub
              </Link>
              <span>/</span>
              <span className="text-white">{title}</span>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-start gap-4">
                {Icon && (
                  <div className="p-3 rounded-xl bg-emerald-900/30">
                    <Icon className="h-6 w-6 text-emerald-400" />
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-bold text-white">{title}</h1>
                  {subtitle && <p className="text-slate-400 mt-1">{subtitle}</p>}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    type="text"
                    placeholder="Search in document..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500 w-56"
                  />
                </div>
                <button
                  onClick={() => window.print()}
                  className="p-2 rounded-lg bg-slate-700/50 hover:bg-slate-700 text-slate-300 transition"
                  title="Print Document"
                >
                  <Printer className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
              <Badge variant="outline" className={`${
                status === 'current' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50' :
                status === 'draft' ? 'bg-amber-500/20 text-amber-300 border-amber-500/50' :
                'bg-slate-500/20 text-slate-300 border-slate-500/50'
              }`}>
                {status === 'current' ? 'Current' : status === 'draft' ? 'Draft' : 'Archived'}
              </Badge>
              <span className="text-slate-500">
                Version: <span className="text-slate-300 font-medium">{version}</span>
              </span>
              <span className="text-slate-600">|</span>
              <span className="text-slate-500 flex items-center gap-1">
                <Clock className="h-4 w-4" />
                Last Reviewed: <span className="text-slate-300">{lastReviewed}</span>
              </span>
              <span className="text-slate-600">|</span>
              <span className="text-slate-500">
                Reviewed by: <span className="text-slate-300">{reviewedBy}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-5xl mx-auto px-6 py-8">
          {/* Table of Contents */}
          {sections.length > 0 && <TableOfContents sections={sections} />}
          
          {/* Page Content */}
          {children}
        </div>

        {/* Print Styles */}
        <style>{`
          @media print {
            body { background: white !important; }
            .bg-\\[\\#0f1a19\\] { background: white !important; }
            .bg-\\[\\#162624\\] { background: #f3f4f6 !important; }
            .bg-slate-800\\/30 { background: #f9fafb !important; border-color: #e5e7eb !important; }
            .bg-slate-900\\/50 { background: #f3f4f6 !important; }
            .text-white { color: black !important; }
            .text-slate-400, .text-slate-300, .text-slate-500 { color: #4b5563 !important; }
            .text-emerald-400, .text-emerald-300 { color: #059669 !important; }
            button, input { display: none !important; }
            nav { page-break-after: always; }
            section { page-break-inside: avoid; }
          }
          
          @page {
            size: A4;
            margin: 2cm;
          }
        `}</style>
      </div>
    </PHSCommandLayout>
  );
}

// Export all components
export { GovernancePageLayout };
